<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import userService from '@/services/userService';
import taskService from '@/services/taskService';
import projectService from '@/services/projectService';
import NotificationMenu from '@/components/notification/NotificationMenu.vue';
import entregavelService from '@/services/entregavelService';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import { usePermissions } from '@/composables/usePermissions';
import BriefingModal from '@/components/modals/BriefingModal.vue'

const { layoutConfig, onMenuToggle } = useLayout();
const { can } = usePermissions();

const outsideClickListener = ref(null);
const topbarMenuActive = ref(false);
const router = useRouter();
const route = useRoute();
const toast = useToast();
const userDisplay = ref('');
const roleDisplay = ref('');

// Mapeamento de grupos do Django para nomes formatados
const GROUP_LABELS = {
  'gerente': 'Gerente',
  'coordenador': 'Coordenador',
  'analista': 'Analista',
  'administrador': 'Administrador',
};

// Estado para header de tarefa no topbar
const taskTopbar = ref(null);
const taskTopbarLoading = ref(false);

// Estado para header de projeto no topbar
const projectTopbar = ref(null);
const projectTopbarLoading = ref(false);

// Lógica de notificações movida para NotificationMenu.vue
// Estado para header de entregável no topbar
const deliverableTopbar = ref(null);
const deliverableTopbarLoading = ref(false);

const showBriefing = ref(false)

onMounted(async () => {
  bindOutsideClickListener();
  let name = localStorage.getItem('nameUser') || '';
  let id = localStorage.getItem('idUser') || '';
  let role = localStorage.getItem('roleDisplay') || '';
  if (name && role) {
    userDisplay.value = name;
    // roleDisplay.value = mapRoles[role];
  }
  if (!userDisplay.value) {
    userService.getInfo().then((response) => {
      const data = response.data;
      
      name = data?.attributes?.name;
      id = data?.id;
      const userIdNumerico = data?.attributes?.idNumerico || data?.attributes?.id_numerico || null;
      
      // Obter grupos do usuário (formato Django)
      const groups = data?.attributes?.groups || [];
      
      localStorage.setItem('nameUser', name);
      localStorage.setItem('idUser', id); // UUID
      
      // Salvar ID numérico se disponível (para notificações privadas)
      if (userIdNumerico) {
        localStorage.setItem('userIdNumerico', userIdNumerico);
        
      } else {
        // FALLBACK: Se backend não retornar idNumerico, buscar na lista de usuários
        import('@/services/usuarioService.ts').then(module => {
          const usuarioService = module.default;
          usuarioService.choice().then(resp => {
            const usuarios = resp.data?.attributes || [];
            // Buscar usuário atual pelo nome (case insensitive)
            const meuUsuario = usuarios.find(u => {
              const match = u.label && u.label.toLowerCase() === name.toLowerCase();
              
              return match;
            });
            
            if (meuUsuario) {
              localStorage.setItem('userIdNumerico', String(meuUsuario.value));
              
            } else {
              if (name === 'Usuário Mock' && usuarios.length > 0) {
                const primeiroUsuario = usuarios[0];
                localStorage.setItem('userIdNumerico', String(primeiroUsuario.value));
              }
            }
          }).catch(err => {
            console.error('[AppTopbar] Erro ao buscar ID numérico:', err);
          });
        });
      }
      
      userDisplay.value = name;
      
      // Usar o primeiro grupo do usuário para exibir no topbar
      if (groups.length > 0) {
        const firstGroup = groups[0];
        roleDisplay.value = GROUP_LABELS[firstGroup] || firstGroup.charAt(0).toUpperCase() + firstGroup.slice(1);
        localStorage.setItem('roleDisplay', roleDisplay.value);
      }
    });
  }
});

onBeforeUnmount(() => {
  unbindOutsideClickListener();
});

const topbarMenuClasses = computed(() => {
  return {
    'layout-topbar-menu-mobile-active': topbarMenuActive.value,
  };
});

const userInitials = computed(() => {
  if (!userDisplay.value) return '';
  const nameParts = userDisplay.value.split(' ');
  if (nameParts.length > 1) {
    return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
  }
  return userDisplay.value.substring(0, 2).toUpperCase();
});

const bindOutsideClickListener = () => {
  if (!outsideClickListener.value) {
    outsideClickListener.value = (event) => {
      if (isOutsideClicked(event)) {
        topbarMenuActive.value = false;
      }
    };
    document.addEventListener('click', outsideClickListener.value);
  }
};
const unbindOutsideClickListener = () => {
  if (outsideClickListener.value) {
    document.removeEventListener('click', outsideClickListener);
    outsideClickListener.value = null;
  }
};
const isOutsideClicked = (event) => {
  if (!topbarMenuActive.value) return;

  const sidebarEl = document.querySelector('.layout-topbar-menu');
  const topbarEl = document.querySelector('.layout-topbar-menu-button');

  return !(
    sidebarEl.isSameNode(event.target) ||
    sidebarEl.contains(event.target) ||
    topbarEl.isSameNode(event.target) ||
    topbarEl.contains(event.target)
  );
};

const createNewProject = async () => {
  // Verifica permissão
  if (!can('sistema.projeto_criar')) {
    toast.error('⛔ Você não tem permissão para criar projetos');
    return;
  }
  
  try {
    const newProjectData = {
      prioridade: 'normal',
      name: '',
      objective: '',
      target_audience: '',
      main_message: '',
      date_start: new Date().toISOString().split('T')[0],
    };
    
    const response = await projectService.create(newProjectData);
    
    if (response && response.data.id) {
      toast.success('Novo projeto criado!');
      router.push(`/projetos/${response.data.id}`);
    }
  } catch (error) {
    console.error('Erro ao criar projeto:', error);
    toast.error('Erro ao criar projeto');
  }
};

const dynamicButton = computed(() => {
  const currentPath = route.path;
  
  if (currentPath === '/projetos') {
    // Só mostra o botão se tiver permissão para criar projeto
    if (!can('sistema.projeto_criar')) {
      return null;
    }
    
    return {
      label: 'Nova Demanda',
      icon: 'pi pi-plus',
      action: createNewProject
    };
  } else if (currentPath === '/usuario') {
    return {
      label: 'Novo Usuário',
      icon: 'pi pi-user-plus',
      action: () => {
        window.dispatchEvent(new CustomEvent('open-user-modal'));
      }
    };
  }
  
  return null;
});

// Helpers de status de tarefa para o badge no topbar
const getStatusClass = (status) => {
  const statusClasses = {
    pendente: 'status-pending',
    'em andamento': 'status-progress',
    'em revisão': 'status-review',
    aprovado: 'status-approved',
  };
  return statusClasses[status] || 'status-default';
};

const getStatusLabel = (status) => {
  const statusLabels = {
    pendente: 'Pendente',
    'em andamento': 'Em andamento',
    'em revisão': 'Em revisão',
    aprovado: 'Aprovado',
  };
  return statusLabels[status] || status;
};

const isTaskDetailRoute = () => {
  // Considera rotas do tipo /tarefas/:id
  return /^\/tarefas\/.+/.test(route.path) && !!route.params.id;
};

const isProjectViewRoute = () => {
  // Considera rotas do tipo /projetos/visualizar/:id
  return /^\/projetos\/visualizar\/.+/.test(route.path) && !!route.params.id;
};

const isDeliverableDetailRoute = () => {
  // Considera rotas do tipo /entregaveis/:id
  return /^\/entregaveis\/.+/.test(route.path) && !!route.params.id;
};

const loadTaskTopbar = async () => {
  if (!isTaskDetailRoute()) {
    taskTopbar.value = null;
    return;
  }
  const id = route.params.id;
  if (!id) return;
  taskTopbarLoading.value = true;
  try {
    const response = await taskService.get(id);
    taskTopbar.value = response.data;
  } catch (e) {
    console.error('Erro ao carregar tarefa no topbar:', e);
    taskTopbar.value = null;
  } finally {
    taskTopbarLoading.value = false;
  }
};

const loadProjectTopbar = async () => {
  if (!isProjectViewRoute()) {
    projectTopbar.value = null;
    return;
  }
  const id = route.params.id;
  if (!id) return;
  projectTopbarLoading.value = true;
  try {
    const response = await projectService.get(id);
    projectTopbar.value = response.data;
  } catch (e) {
    console.error('Erro ao carregar projeto no topbar:', e);
    projectTopbar.value = null;
  } finally {
    projectTopbarLoading.value = false;
  }
};

const loadDeliverableTopbar = async () => {
  if (!isDeliverableDetailRoute()) {
    deliverableTopbar.value = null;
    return;
  }
  const id = route.params.id;
  if (!id) return;
  deliverableTopbarLoading.value = true;
  try {
    const response = await entregavelService.get(id);
    deliverableTopbar.value = response.data;
  } catch (e) {
    console.error('Erro ao carregar entregável no topbar:', e);
    deliverableTopbar.value = null;
  } finally {
    deliverableTopbarLoading.value = false;
  }
};

// Carrega inicialmente e quando a rota mudar
onMounted(async () => {
  loadTaskTopbar();
  loadProjectTopbar();
  loadDeliverableTopbar();
});

watch(
  () => route.fullPath,
  () => {
    loadTaskTopbar();
    loadProjectTopbar();
    loadDeliverableTopbar();
  }
);

const goBackFromTopbar = () => {
  router.push('/tarefas');
};

const goBackFromProjectTopbar = () => {
  router.push('/projetos');
};

const goBackFromDeliverableTopbar = () => {
  router.push('/entregaveis');
};

const getProjectViewUrl = (projectId) => {
  return router.resolve({ name: 'project-view', params: { id: projectId } }).href;
};

const getProjectStatusClass = (status) => {
  const statusClasses = {
    rascunho: 'status-rascunho',
    a_fazer: 'status-a-fazer',
    fazendo: 'status-fazendo',
    em_revisao: 'status-em-revisao',
    concluido: 'status-concluido',
  };
  return statusClasses[status] || 'status-default';
};

const getProjectStatusLabel = (status) => {
  const statusLabels = {
    rascunho: 'Rascunho',
    a_fazer: 'A Fazer',
    fazendo: 'Fazendo',
    em_revisao: 'Revisando',
    concluido: 'Concluído',
  };
  return statusLabels[status] || status;
};

</script>

<template>
  <div class="layout-topbar">
    <router-link to="/" class="layout-topbar-logo">
      <span class="logo-badge">CM</span>
      <span class="brand-text">Comunicação</span>
    </router-link>

    <button
      class="p-link layout-menu-button layout-topbar-button"
      @click="onMenuToggle()"
    >
      <i class="pi pi-bars"></i>
    </button>

    <!-- Header de Projeto dentro do Topbar -->
    <div v-if="projectTopbar" class="task-topbar-info">
      <Button
        icon="pi pi-arrow-left"
        label="Voltar"
        text
        class="back-btn"
        @click="goBackFromProjectTopbar"
      />
      <i class="pi pi-folder" style="font-size: 24px;"></i>
      <div class="task-info">
        <h1 class="task-title">
          <span class="project-code-prefix">{{ projectTopbar?.projectCode || 'Não encontrado' }}</span>
          <span class="project-name-text">{{ projectTopbar?.name || '' }}</span>
        </h1>
        <div class="task-meta">
          <div class="task-status-badge" :class="projectTopbar?.statusClass">
            {{ projectTopbar?.statusFormatted || 'Não encontrado' }}
          </div>
        </div>
      </div>
      <div class="header-actions">
        <Button icon="pi pi-trash" label="Excluir" class="action-btn danger" />
      </div>
    </div>

    <!-- Header de Entregável dentro do Topbar -->
    <div v-else-if="deliverableTopbar" class="task-topbar-info">
      <Button 
        icon="pi pi-arrow-left" 
        label="Voltar"
        text 
        class="back-btn"
        @click="goBackFromDeliverableTopbar"
      />
      <i class="pi pi-box" style="font-size: 18px;"></i>
      <div class="task-info">
        <h1 class="task-title">{{ deliverableTopbar?.attributes?.name || 'Carregando...' }}</h1>
        <div class="task-meta">
          <div class="project-id-badge">{{ deliverableTopbar?.attributes?.codigoProjeto || deliverableTopbar?.attributes?.codigo_projeto || 'N/A' }}</div>
          <div class="task-status-badge" :class="getStatusClass(deliverableTopbar?.attributes?.status_calculado || deliverableTopbar?.attributes?.status)">
            {{ getStatusLabel(deliverableTopbar?.attributes?.status_calculado || deliverableTopbar?.attributes?.status) }}
          </div>
        </div>
      </div>
      <div class="header-actions">
        <Button icon="pi pi-trash" label="Excluir" class="action-btn danger" />
      </div>
    </div>

    <!-- Header de Tarefa dentro do Topbar -->
    <div v-else-if="taskTopbar" class="task-topbar-info">
      <Button
        icon="pi pi-arrow-left"
        label="Voltar"
        text
        class="back-btn"
        @click="goBackFromTopbar"
      />
      <i class="pi pi-list-check" style="font-size: 18px;"></i>
      <div class="task-info">
        <h1 class="task-title">
          <a 
            v-if="taskTopbar?.attributes?.project?.id"
            :href="getProjectViewUrl(taskTopbar.attributes.project.id)"
            target="_blank"
            class="project-link"
          >
            <span class="project-code-prefix">{{ taskTopbar?.attributes?.project?.projectCode || taskTopbar?.attributes?.project?.project_code || 'N/A' }}</span>
            <span class="project-name-text">{{ taskTopbar?.attributes?.project?.name || 'Carregando...' }}</span>
            <i class="pi pi-external-link" style="font-size: 0.7rem; margin-left: 0.25rem;"></i>
          </a>
          <template v-else>
            <span class="project-code-prefix">{{ taskTopbar?.attributes?.project?.projectCode || taskTopbar?.attributes?.project?.project_code || 'N/A' }}</span>
            <span class="project-name-text">{{ taskTopbar?.attributes?.project?.name || 'Carregando...' }}</span>
          </template>
        </h1>
        <div class="task-meta">
          <div class="task-status-badge" :class="getStatusClass(taskTopbar?.attributes?.status)">
            {{ getStatusLabel(taskTopbar?.attributes?.status) }}
          </div>
        </div>
      </div>
      <div class="header-actions">
        <!-- <Button icon="pi pi-copy" text label="Duplicar" class="action-btn secondary" />
        <Button icon="pi pi-share-alt" text label="Compartilhar" class="action-btn secondary" /> -->
        <Button
          icon="pi pi-book"
          label="Ver briefing"
          text
          class="action-btn secondary"
          @click="showBriefing = true"
        />
        <Button icon="pi pi-trash" label="Excluir" class="action-btn danger" />
      </div>
    </div>

    <div class="layout-topbar-menu" :class="topbarMenuClasses">
      <!-- Botão Dinâmico -->
      <div v-if="dynamicButton" class="dynamic-button-container">
        <button
          class="dynamic-action-button"
          @click="dynamicButton.action"
        >
          <i :class="dynamicButton.icon"></i>
          <span>{{ dynamicButton.label }}</span>
        </button>
      </div>

      <!-- ✅ Componente de Notificações -->
      <NotificationMenu />

      <!-- Perfil do Usuário -->
      <div class="user-profile">
        <span 
          class="initials" 
          v-tooltip.bottom="userDisplay"
        >
          {{ userInitials }}
        </span>
      </div>
      
      <!-- <button @click="onSettingsClick()" class="p-link layout-topbar-button">
        <i class="pi pi-cog"></i>
        <span>Settings</span>
      </button> -->
    </div>
  </div>

  <BriefingModal
    v-model="showBriefing"
    :content="taskTopbar?.attributes?.project?.objective || '<p>Não informado</p>'"
  />

</template>

<style lang="scss" scoped src="./styles/AppTopbar.styles.scss"></style>
